// EdAI Express page content. Positioning rule from the brief: Express does NOT
// carry the Standard-of-Knowledge provenance claim. Its differentiator is that
// the tools work on anything the learner brings. Never imply board validation.
// House rules: no em-dashes anywhere.

export const liveUrl = 'https://edai-express.com';

// The interactive specimen: one source excerpt, restyled per exam family.
// All sample content is illustrative output, labeled as such on the page.
export const tuningSpecimen = {
  sourceLabel: 'Your upload \u00b7 cardio_pharm_lecture.pdf \u00b7 page 12',
  sourceText:
    'Beta-adrenergic antagonists reduce myocardial oxygen demand by lowering heart rate and contractility. Cardioselective agents (metoprolol, bisoprolol, atenolol) preferentially block \u03b21 receptors at therapeutic doses. In heart failure with reduced ejection fraction, evidence-based beta-blockade is titrated to target doses as tolerated; bradycardia and hypotension limit up-titration.',
  families: [
    {
      id: 'usmle2',
      name: 'USMLE Step 2 CK',
      question: {
        stem:
          'A 62-year-old man with heart failure with reduced ejection fraction returns two weeks after starting metoprolol succinate. He reports lightheadedness on standing. Pulse is 52/min and regular; blood pressure is 104/68 mm Hg. Which of the following is the most appropriate next step in management?',
        options: [
          { key: 'A', text: 'Continue the current dose and reassess in four weeks' },
          { key: 'B', text: 'Reduce the metoprolol dose' },
          { key: 'C', text: 'Switch metoprolol to verapamil' },
          { key: 'D', text: 'Add ivabradine' },
        ],
        correctKey: 'B',
        explanation:
          'Symptomatic bradycardia during beta-blocker up-titration warrants dose reduction rather than discontinuation, preserving mortality benefit while relieving symptoms. Verapamil adds nodal blockade and worsens the problem; ivabradine lowers heart rate further and is considered only at adequate beta-blockade without symptomatic bradycardia.',
      },
      guide: [
        'Mechanism first: \u03b21 blockade lowers rate, contractility, and myocardial oxygen demand.',
        'HFrEF titration: start low, go slow, target evidence-based doses as tolerated.',
        'Boards pattern: symptomatic bradycardia during titration is managed by dose reduction, not abrupt withdrawal.',
      ],
    },
    {
      id: 'nclex',
      name: 'NCLEX-RN',
      question: {
        stem:
          'A nurse prepares to administer the morning dose of metoprolol to a client with heart failure. Which assessment finding requires the nurse to withhold the dose and notify the provider?',
        options: [
          { key: 'A', text: 'Blood pressure 128/76 mm Hg' },
          { key: 'B', text: 'Apical pulse 54/min' },
          { key: 'C', text: 'Respiratory rate 18/min' },
          { key: 'D', text: 'Temperature 37.2 \u00b0C' },
        ],
        correctKey: 'B',
        explanation:
          'Beta-blockers slow conduction and heart rate; an apical pulse below 60/min is the classic hold-and-notify parameter. The remaining findings are within expected limits and do not contraindicate administration.',
      },
      guide: [
        'Assess before you administer: apical pulse for a full minute; hold below 60/min and notify.',
        'Client teaching: never stop abruptly; report dizziness, weight gain, or worsening breathlessness.',
        'Safety framing: orthostatic precautions during dose changes.',
      ],
    },
    {
      id: 'plab',
      name: 'PLAB',
      question: {
        stem:
          'A 68-year-old man with heart failure attends the GP surgery two weeks after starting bisoprolol. He describes dizziness on standing. His pulse is 50 bpm and regular; blood pressure is 102/66 mm Hg. What is the most appropriate management?',
        options: [
          { key: 'A', text: 'Continue the current dose and review in one month' },
          { key: 'B', text: 'Reduce the bisoprolol dose' },
          { key: 'C', text: 'Stop bisoprolol immediately' },
          { key: 'D', text: 'Switch to diltiazem' },
        ],
        correctKey: 'B',
        explanation:
          'Symptomatic bradycardia during beta-blocker titration is managed by dose reduction while maintaining the prognostic benefit of beta-blockade. Abrupt cessation risks rebound; rate-limiting calcium-channel blockade compounds the bradycardia.',
      },
      guide: [
        'UK register: bisoprolol is the exam\u2019s beta-blocker of habit; know its titration ladder.',
        'Management-first stems: the safe adjustment beats the dramatic intervention.',
        'Red flags to escalate: syncope, heart block, decompensating failure.',
      ],
    },
  ],
  flashcard: {
    front: 'At therapeutic doses, which receptor does metoprolol preferentially block?',
    back: '\u03b21 (cardioselective)',
    note: 'Flashcards are exam-agnostic by design; the exam-family selector applies to questions and guides.',
  },
};

export const examFamilies = [
  'USMLE Step 1',
  'USMLE Step 2 CK',
  'USMLE Step 3',
  'NCLEX-RN',
  'MCCQE',
  'PLAB',
  'AMC MCQ',
  'DHA / HAAD / MOHAP',
];

export const howItWorks = [
  {
    name: 'Upload',
    text: 'Drag in a PDF: a lecture deck, a chapter, your notes. Up to 50 MB and 50 pages, text-extractable. Extraction runs automatically; a status dot tells you honestly how it went, including the amber state where the text layer was unusable and the pages were read visually instead.',
  },
  {
    name: 'Choose',
    text: 'Pick an output: exam-style questions with explanations, a structured study guide, or flashcards. Pick your exam family so tone and style match the exam you are actually sitting. The credit cost updates live before you commit.',
  },
  {
    name: 'Study or take it with you',
    text: 'Generation typically takes 10 to 30 seconds. Work the questions one at a time with keyboard shortcuts, read the guide, drill the cards, or export everything: a real Anki .apkg, PDF, or Markdown. Every export is free.',
  },
];

export const outputs = [
  {
    name: 'Practice questions',
    text: 'Exam-style multiple choice with explanations, 3 to 20 per run, tuned to your exam family. One credit per question.',
  },
  {
    name: 'Study guides',
    text: 'Summary, key concepts, worked examples, and common pitfalls, structured from your own material. Flat 10 credits.',
  },
  {
    name: 'Flashcards',
    text: '20, 50, or 100 front-and-back cards, exportable as a real Anki .apkg file. 5, 10, or 20 credits.',
  },
];

export const creditPacks = [
  { name: 'Starter', price: '$10', credits: '100 credits' },
  { name: 'Plus', price: '$25', credits: '275 credits' },
  { name: 'Pro', price: '$60', credits: '720 credits' },
  { name: 'Max', price: '$150', credits: '1,950 credits' },
];

export const proPlans = [
  { name: 'Pro Monthly', price: '$19.99/mo for the first 3 months, then $29.99/mo' },
  { name: 'Pro Annual', price: '$299/yr' },
];

export const pricingNotes = [
  'Credits never expire, and every export (Anki, PDF, Markdown) is free.',
  'Free and pay-as-you-go accounts keep one active upload, removed 24 hours after upload; Pro keeps your library with 10 GB of storage.',
  'If a generation fails, the credits are refunded automatically: a real refund transaction in your account history, not a message.',
];

export const honesty = [
  {
    title: 'No provenance claim, on purpose',
    text: 'Express generates from your uploads, not from board-designated sources. It does not carry the Standard-of-Knowledge provenance guarantees of EdAI\u2019s authority-derived products, and it never pretends to. The output is only as good as the material you bring it.',
  },
  {
    title: 'Failures refund themselves',
    text: 'If a generation fails, the credits come back automatically, recorded as a refund transaction in your history. You should not have to ask for your money back when the machine fumbles.',
  },
  {
    title: 'The status dot tells the truth',
    text: 'A green dot means your PDF\u2019s text extracted cleanly. Amber means the text layer was unusable and the pages were read visually instead, which is worth knowing before you trust the output. Red means it failed, plainly, with what to do next.',
  },
  {
    title: 'Limits stated before you hit them',
    text: 'PDF only, 50 MB, 50 pages, text-extractable: v1 does not OCR pure image scans, and the product says so up front rather than failing mysteriously. The free tier\u2019s 24-hour retention is announced before you upload, not after.',
  },
];

export const faq = [
  {
    q: 'Is Express content board-validated?',
    a: 'No, and we will not imply it. Express is the modality engine on its own: it transforms whatever you upload. If you want preparation built on board-designated, physician-certified sources, that is EdAI Prep, a different product with a different guarantee.',
  },
  {
    q: 'What files can I upload?',
    a: 'PDFs up to 50 MB and 50 pages, with an extractable text layer. When the text layer is unusable, a visual fallback reads the page images and the status dot shows amber so you know. True image-only scans with no text at all are not supported in v1.',
  },
  {
    q: 'What do exports cost?',
    a: 'Nothing. Anki .apkg, PDF, and Markdown exports are free, always. Credits pay for generation: one per question, ten for a study guide, five to twenty for a flashcard deck by size.',
  },
  {
    q: 'What happens if a generation fails?',
    a: 'Your credits are refunded automatically, and the refund appears as a transaction in your account history. Then try again or upload a different file.',
  },
  {
    q: 'How is this different from EdAI Prep?',
    a: 'Prep is authority-derived: questions generated from a physician-certified corpus scoped to your specific exam, with provenance on every item. Express is material-derived: your own uploads, transformed fast, for any exam family and, by design, any domain. Different jobs; pick by what you need.',
  },
  {
    q: 'Is it open to everyone?',
    a: 'Express is live in closed beta. Beta codes unlock access without a card; credit packs and Pro subscriptions are available at edai-express.com. Institutions and partners can ask us about coupon-powered access for cohorts.',
  },
];
